/**
 * Knowledge Bases list page — CRUD for knowledge bases + document upload.
 */
import { useState, useCallback } from 'react';
import {
  Grid,
  Column,
  Button,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Modal,
  TextInput,
  TextArea,
  InlineLoading,
  IconButton,
} from '@carbon/react';
import { Add, TrashCan, DocumentAdd, View } from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  useKnowledgeBases,
  useCreateKnowledgeBase,
  useDeleteKnowledgeBase,
} from '@/lib/query/hooks';
import type { KnowledgeBase } from '@shared/types/knowledge-base';

const headers = [
  { key: 'name', header: 'Name' },
  { key: 'description', header: 'Description' },
  { key: 'type', header: 'Type' },
  { key: 'knowledge_object_count', header: 'Assets' },
  { key: 'created_at', header: 'Created' },
  { key: 'actions', header: '' },
];

export default function KnowledgeBasesPage() {
  const navigate = useNavigate();
  const { data: knowledgeBases, isLoading } = useKnowledgeBases();
  const createMutation = useCreateKnowledgeBase();
  const deleteMutation = useDeleteKnowledgeBase();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeBase | null>(null);

  const handleCreate = useCallback(async () => {
    if (!name.trim()) return;
    await createMutation.mutateAsync({ name: name.trim(), description: description.trim() || undefined });
    setName('');
    setDescription('');
    setCreateOpen(false);
  }, [name, description, createMutation]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  }, [deleteTarget, deleteMutation]);

  const rows = (knowledgeBases || []).map((kb) => ({
    id: kb.id,
    name: kb.name,
    description: kb.description || '—',
    type: kb.type,
    knowledge_object_count: kb.knowledge_object_count ?? 0,
    created_at: new Date(kb.created_at).toLocaleDateString(),
    actions: kb,
  }));

  return (
    <Grid fullWidth style={{ padding: '2rem 0' }}>
      <Column lg={16} md={8} sm={4}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 600 }}>Knowledge Bases</h1>
          <Button renderIcon={Add} onClick={() => setCreateOpen(true)}>
            New Knowledge Base
          </Button>
        </div>

        {isLoading ? (
          <InlineLoading description="Loading knowledge bases..." />
        ) : rows.length === 0 ? (
          <p style={{ color: 'var(--cds-text-secondary)' }}>
            No knowledge bases yet. Create one to get started.
          </p>
        ) : (
          <DataTable rows={rows} headers={headers}>
            {({ rows: tableRows, headers: tableHeaders, getTableProps, getHeaderProps, getRowProps }) => (
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.map((row) => {
                    const kb = (knowledgeBases || []).find((k) => k.id === row.id)!;
                    return (
                      <TableRow
                        {...getRowProps({ row })}
                        key={row.id}
                        onClick={() => navigate(`/knowledge-bases/${kb.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {row.cells.map((cell) => {
                          if (cell.info.header === 'type') {
                            return (
                              <TableCell key={cell.id}>
                                <Tag type="blue" size="sm">{String(cell.value)}</Tag>
                              </TableCell>
                            );
                          }
                          if (cell.info.header === 'actions') {
                            return (
                              <TableCell key={cell.id} onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  <IconButton
                                    kind="ghost"
                                    size="sm"
                                    label="View documents"
                                    onClick={() => navigate(`/knowledge-bases/${kb.id}`)}
                                  >
                                    <View />
                                  </IconButton>
                                  <IconButton
                                    kind="ghost"
                                    size="sm"
                                    label="Upload document"
                                    onClick={() => navigate(`/knowledge-bases/${kb.id}`)}
                                  >
                                    <DocumentAdd />
                                  </IconButton>
                                  <IconButton
                                    kind="ghost"
                                    size="sm"
                                    label="Delete"
                                    onClick={() => setDeleteTarget(kb)}
                                  >
                                    <TrashCan style={{ color: 'var(--cds-support-error)' }} />
                                  </IconButton>
                                </div>
                              </TableCell>
                            );
                          }
                          return <TableCell key={cell.id}>{String(cell.value)}</TableCell>;
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </DataTable>
        )}
      </Column>

      {/* Create Modal */}
      <Modal
        open={createOpen}
        modalHeading="Create Knowledge Base"
        primaryButtonText="Create"
        secondaryButtonText="Cancel"
        onRequestClose={() => setCreateOpen(false)}
        onRequestSubmit={handleCreate}
        primaryButtonDisabled={!name.trim() || createMutation.isPending}
      >
        <TextInput
          id="kb-name"
          data-testid="kb-name-input"
          labelText="Name"
          placeholder="e.g. Drug Discovery Papers"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ marginBottom: '1rem' }}
        />
        <TextArea
          id="kb-description"
          labelText="Description (optional)"
          placeholder="Brief description of this knowledge base"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={!!deleteTarget}
        danger
        modalHeading="Delete Knowledge Base"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={handleDelete}
        primaryButtonDisabled={deleteMutation.isPending}
      >
        <p>
          Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          This will permanently remove the knowledge base and all its documents.
        </p>
      </Modal>
    </Grid>
  );
}

// Made with Bob
